import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Select, Btn, Input, EmptyState } from "@/components/kit";
import { FleetDashboard } from "@/components/FleetDashboard";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { normalizeReg } from "@/lib/vda";

const ALL_MODULES = [
  ["attention_now", "Needs attention now"],
  ["forward_service_mot", "Service & MOT due"],
  ["open_recalls", "Open recalls"],
  ["contract_ending", "Contracts ending"],
  ["compliance_expiry", "Compliance expiring"],
  ["weekly_checks", "Weekly checks"],
  ["open_requests", "Open requests"],
];
const THRESHOLDS = [
  ["service_due_days", "Service window (days)"],
  ["mot_due_days", "MOT window (days)"],
  ["contract_end_days", "Contract window (days)"],
  ["compliance_expiry_days", "Compliance window (days)"],
  ["compliance_imminent_days", "Compliance urgent (days)"],
];

export default function AMView() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vehMap, setVehMap] = useState({});
  const [showCfg, setShowCfg] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadDash = (cid) => {
    setLoading(true);
    Promise.all([
      api.fmDashboard(cid),
      supabase.from("vehicles").select("id, registration").eq("customer_id", cid),
    ]).then(([d, { data: vs }]) => {
      setDash(d);
      setDraft(JSON.parse(JSON.stringify(d.config)));
      const m = {}; (vs || []).forEach((v) => { m[normalizeReg(v.registration)] = v.id; });
      setVehMap(m);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.amCustomers().then((r) => {
      setCustomers(r.customers || []);
      if (r.customers?.length) setCustomerId(r.customers[0].id);
    });
  }, []);

  useEffect(() => {
    if (!customerId) return;
    setShowCfg(false);
    loadDash(customerId);
  }, [customerId]);

  const toggleModule = (key) => setDraft((d) => ({ ...d, modules: d.modules.includes(key) ? d.modules.filter((m) => m !== key) : [...d.modules, key] }));
  const setThreshold = (key, val) => setDraft((d) => ({ ...d, thresholds: { ...d.thresholds, [key]: Number(val) || 0 } }));
  const setHorizons = (val) => setDraft((d) => ({ ...d, horizons: val.split(",").map((x) => Number(x.trim())).filter((n) => n > 0) }));
  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.setDashboardConfig({ customer_id: customerId, modules: draft.modules, thresholds: draft.thresholds, horizons: draft.horizons });
      toast.success("Dashboard configuration saved.");
      setShowCfg(false);
      loadDash(customerId);
    } catch (e) { toast.error(e.message || "Could not save configuration."); }
    finally { setSaving(false); }
  };

  const openVehicle = (reg) => { const id = vehMap[normalizeReg(reg || "")]; if (id) nav(`/staff/vehicle/${id}`); };
  const current = useMemo(() => (customers || []).find((c) => c.id === customerId), [customers, customerId]);

  if (customers === null) return <StaffShell><Spinner /></StaffShell>;

  return (
    <StaffShell>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="vda-tick text-[hsl(var(--vda-clay))]">Account manager</p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Customer fleet view</h1>
          <p className="text-sm text-muted-foreground">The same configured dashboard your customers see — scoped to the customer you select.</p>
        </div>
        <div className="min-w-[240px]">
          <label className="vda-tick text-[hsl(var(--vda-quiet))] block mb-1">Customer</label>
          <div className="flex items-center gap-2">
            <Select data-testid="am-customer-switch" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Btn variant="outline" data-testid="am-configure-btn" onClick={() => setShowCfg((v) => !v)} disabled={!dash}><Settings2 className="h-4 w-4" /> Configure</Btn>
          </div>
        </div>
      </div>

      {showCfg && draft && (
        <Panel className="p-5 mb-5" data-testid="am-config-panel">
          <h2 className="font-semibold mb-3">Dashboard configuration — {current?.name}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="vda-tick text-[hsl(var(--vda-quiet))] mb-2">Visible modules</p>
              <div className="flex flex-col gap-1.5">
                {ALL_MODULES.map(([key, label]) => (
                  <label key={key} className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid={`am-cfg-module-${key}`}>
                    <input type="checkbox" checked={draft.modules.includes(key)} onChange={() => toggleModule(key)} className="h-4 w-4 accent-[hsl(var(--primary))]" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="vda-tick text-[hsl(var(--vda-quiet))] mb-2">Thresholds</p>
              <div className="grid grid-cols-2 gap-2">
                {THRESHOLDS.map(([key, label]) => (
                  <div key={key}>
                    <label className="text-xs text-muted-foreground">{label}</label>
                    <Input type="number" data-testid={`am-cfg-threshold-${key}`} value={draft.thresholds[key] ?? ""} onChange={(e) => setThreshold(key, e.target.value)} />
                  </div>
                ))}
              </div>
              <p className="vda-tick text-[hsl(var(--vda-quiet))] mt-4 mb-2">Look-ahead horizons (days, comma-separated)</p>
              <Input data-testid="am-cfg-horizons" defaultValue={draft.horizons.join(", ")} onBlur={(e) => setHorizons(e.target.value)} placeholder="7, 30, 90" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Btn variant="outline" onClick={() => setShowCfg(false)}>Cancel</Btn>
            <Btn data-testid="am-cfg-save" onClick={saveConfig} disabled={saving}>{saving ? "Saving…" : "Save configuration"}</Btn>
          </div>
        </Panel>
      )}

      {customers.length === 0 ? <EmptyState title="No customers assigned to you" /> : loading || !dash ? (
        <Panel className="p-10 grid place-items-center"><Spinner /></Panel>
      ) : (
        <div data-testid="am-dashboard">
          <p className="vda-tick text-[hsl(var(--vda-quiet))] mb-2">{current?.name}</p>
          <FleetDashboard data={dash} onOpenVehicle={openVehicle} />
        </div>
      )}
    </StaffShell>
  );
}
